import CardWrapper from '@/components/common/CardWrapper/CardWrapper';
import ImageCropper from '@/components/common/ImageCropper/cropper';
import {
  profileCompletionDefaultValues,
  profileCompletionSchema,
} from '@/components/common/UpdateProfile/Schemas';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/sonner';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { CustomTextarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { UPDATE_USER } from '@/Graphql/mutation';
import useAwsUpload from '@/hooks/useAwsUpload';
import { useGetCurrentUser } from '@/hooks/useGetCurrentUser';
import { EQUIPMENT_AT_HOME, LANGUAGE } from '@/lib/constants';
import { ageRangeOptions, handleError } from '@/lib/utils';
import { CREATE_SESSION } from '@/pages/sessions/GraphQl/mutation';
import { getRoutes } from '@/routes/routePaths';
import { FileType, Gender } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useMutation, useReactiveVar } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import imageCompression from 'browser-image-compression';

import { CameraIcon, UserRound, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

export type ProfileCompletionSchemaType = z.infer<typeof profileCompletionSchema>;

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const { upload } = useAwsUpload();
  const { getCurrentUser } = useGetCurrentUser();
  const { userDetails } = useReactiveVar(authState);

  // State for pending session data
  const [pendingSessionData, setPendingSessionData] = useState<any>(null);

  const [createSession] = useMutation(CREATE_SESSION, {
    onCompleted: () => {
      showToast({ message: 'Session created successfully', variant: 'success' });
      // Clear pending session data
      localStorage.removeItem('pendingSessionData');
      setPendingSessionData(null);
    },
    onError: handleError,
    refetchQueries: ['GetAllSession', 'GetSessionBalance'],
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: async () => {
      await getCurrentUser();
      // showToast({ variant: 'success', message: 'Profile completed successfully' });

      // If there's pending session data, create the session
      if (pendingSessionData) {
        try {
          await createSession({
            variables: {
              data: pendingSessionData,
            },
          });
        } catch (error) {
          // console.error('Error creating session:', error);
        }
      }

      navigate(getRoutes.home);
    },
    onError: handleError,
    refetchQueries: ['GetUserById', 'GetCurrentUser'],
  });



  const methods = useForm<ProfileCompletionSchemaType>({
    resolver: zodResolver(profileCompletionSchema),
    defaultValues: profileCompletionDefaultValues,
  });

  const [loading, setLoading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [croppedImage, setCroppedImage] = useState<File | undefined>(undefined);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const imageRef = useRef<HTMLInputElement>(null);

  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = methods;
  console.log('errors', errors)
  useEffect(() => {
    methods.setValue('equipments', [EQUIPMENT_AT_HOME[0]]);

    // Check for pending session data
    const storedSessionData = localStorage.getItem('pendingSessionData');
    if (storedSessionData) {
      try {
        const sessionData = JSON.parse(storedSessionData);
        setPendingSessionData(sessionData);
      } catch (error) {
        // console.error('Error parsing pending session data:', error);
        localStorage.removeItem('pendingSessionData');
      }
    }
  }, []);

  const handleImageClick = () => {
    if (selectedImage || profileImageUrl) {
      setProfileImageUrl('');
      setSelectedImage(undefined);
      setCroppedImage(undefined);
    } else {
      imageRef.current?.click();
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
          fileType: 'image/jpeg',
        };
        const compressedFile = await imageCompression(file, options);
        setSelectedImage(compressedFile);
        setCropperOpen(true);
        if (imageRef.current) {
          imageRef.current.value = '';
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        // console.error('Error compressing image:', error);
      }
    }
  };

  const onSubmit = methods.handleSubmit(async (data) => {
    try {
      setLoading(true);
      let uploadedImage;
      if (croppedImage) {
        const selectedFile = {
          name: croppedImage?.name,
          file: croppedImage,
        };
        uploadedImage = await upload(selectedFile, FileType.Photo);
      }

      await updateUser({
        variables: {
          data: {
            _id: userDetails?._id,
            ...(uploadedImage?.key && { profileImage: uploadedImage.key }),
            ...(profileImageUrl.length === 0 && !uploadedImage?.key && { profileImage: null }),
            gender: data.gender,
            ageRange: data.ageRange,
            languages: data.languages,
            equipments: data.equipments,
            currentEngagements: {
              description: data.physicalActivities,
              status: data.isPhysicalActivities,
            },
          },
        },
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <div className='w-full flex flex-col min-h-screen bg-bgPrimary'>
          <div className='w-full flex justify-center pt-8 pb-16'>
            <div className='w-[95%] max-w-[640px] flex flex-col gap-6'>
              {/* Profile Picture */}
              <div className='flex justify-center'>
                <div className='relative w-min m-5'>
                  <div className='flex items-center justify-center h-[100px] w-[100px] rounded-full bg-slate-600 overflow-hidden'>
                    <Avatar className='w-full h-full'>
                      <AvatarImage
                        src={
                          profileImageUrl
                            ? profileImageUrl
                            : croppedImage
                              ? URL.createObjectURL(croppedImage as Blob)
                              : ''
                        }
                        alt='avatar'
                        className='rounded-full w-full'
                      />
                      <AvatarFallback className='flex items-center justify-center'>
                        <UserRound color='#fff' size={44} />
                      </AvatarFallback>
                    </Avatar>
                    <Button
                      id='image-upload-btn'
                      variant='ghost'
                      className='flex items-center justify-center w-8 h-8 absolute top-[-5px] right-0 rounded-full p-0 bg-[#343840] text-white'
                      type='button'
                      onClick={handleImageClick}
                    >
                      {profileImageUrl || croppedImage ? <X className='w-5 h-5' /> : <CameraIcon />}
                    </Button>
                    <input
                      type='file'
                      className='hidden'
                      accept='.jpg, .png, .jpeg, .webp'
                      id='profile-completion-image'
                      ref={imageRef}
                      onChange={handleImageChange}
                    />
                  </div>
                </div>
              </div>

              {/* Gender Selection */}
              <CardWrapper>
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label className='text-white'>
                      Select Gender<span className='text-[#DC1D00]'>*</span>
                    </Label>
                    <p className='text-[#A8ADB9] text-sm'>
                      Which gender do you more closely identify with?
                    </p>
                  </div>
                  <ToggleGroup
                    type='single'
                    value={watch('gender')}
                    onValueChange={(value) =>
                      value && setValue('gender', value, { shouldValidate: true })
                    }
                    className='grid grid-cols-3 gap-2'
                  >
                    <ToggleGroupItem
                      value={Gender.Male}
                      className='bg-zinc-700 data-[state=on]:bg-white data-[state=on]:text-black'
                    >
                      Male
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value={Gender.Female}
                      className='bg-zinc-700 data-[state=on]:bg-white data-[state=on]:text-black'
                    >
                      Female
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value={Gender.NonBinary}
                      className='bg-zinc-700 data-[state=on]:bg-white data-[state=on]:text-black'
                    >
                      Non-Binary
                    </ToggleGroupItem>
                  </ToggleGroup>
                  {errors?.gender?.message && (
                    <p className='text-red text-xs'>{errors?.gender?.message}</p>
                  )}
                </div>
              </CardWrapper>

              {/* Age Range */}
              <CardWrapper>
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label className='text-white'>
                      Pick Your Age Range<span className='text-[#DC1D00]'>*</span>
                    </Label>
                    <p className='text-[#A8ADB9] text-sm'>
                      Please select your age group. FitStatic is available for users aged 13 and
                      above.
                    </p>
                  </div>
                  <ToggleGroup
                    type='single'
                    value={watch('ageRange')}
                    onValueChange={(value) =>
                      value && setValue('ageRange', value, { shouldValidate: true })
                    }
                    className='grid grid-cols-3 gap-2'
                  >
                    {ageRangeOptions.map((option) => (
                      <ToggleGroupItem
                        key={option.value}
                        value={option.value}
                        className='bg-zinc-700 data-[state=on]:bg-white data-[state=on]:text-black text-sm'
                      >
                        {option.text}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                  {errors?.ageRange?.message && (
                    <p className='text-red text-xs'>{errors?.ageRange?.message}</p>
                  )}
                </div>
              </CardWrapper>

              {/* Languages */}
              <CardWrapper>
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label className='text-white'>
                      Languages You Know<span className='text-[#DC1D00]'>*</span>
                    </Label>
                    <p className='text-[#A8ADB9] text-sm'>
                      Pick the languages you&apos;re comfortable with to find the right trainers and
                      resources.
                    </p>
                  </div>
                  <ToggleGroup
                    value={watch('languages')}
                    onValueChange={(value) =>
                      setValue('languages', value, { shouldValidate: true })
                    }
                    type='multiple'
                    className='grid grid-cols-4 gap-2'
                  >
                    {LANGUAGE.map((language) => (
                      <ToggleGroupItem key={language.value} value={language.value}>
                        {language.label}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                  {errors?.languages?.message && (
                    <p className='text-red text-xs'>{errors?.languages?.message}</p>
                  )}
                </div>
              </CardWrapper>



              {/* Equipment */}
              <CardWrapper>
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label className='text-white'>Do you have any equipment at home?</Label>
                    <p className='text-[#A8ADB9] text-sm'>(You can select multiple items)</p>
                  </div>
                  <ToggleGroup
                    value={watch('equipments')}
                    onValueChange={(val) => {
                      if (
                        val.includes(EQUIPMENT_AT_HOME[0]) &&
                        watch('equipments')?.includes(EQUIPMENT_AT_HOME[0])
                      ) {
                        setValue(
                          'equipments',
                          val.filter((item) => item !== EQUIPMENT_AT_HOME[0]),
                          { shouldValidate: true },
                        );
                      } else if (
                        val.includes(EQUIPMENT_AT_HOME[0]) &&
                        !watch('equipments')?.includes(EQUIPMENT_AT_HOME[0])
                      ) {
                        setValue('equipments', [EQUIPMENT_AT_HOME[0]], { shouldValidate: true });
                      } else if (val.length === 0) {
                        setValue('equipments', [EQUIPMENT_AT_HOME[0]], { shouldValidate: true });
                      } else {
                        setValue('equipments', val, { shouldValidate: true });
                      }
                    }}
                    type='multiple'
                    className='grid grid-cols-3 gap-2'
                  >
                    {EQUIPMENT_AT_HOME.map((type) => (
                      <ToggleGroupItem key={type} value={type}>
                        {type}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>
              </CardWrapper>



              {/* Additional Information */}
              <CardWrapper>
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label className='text-white'>Any additional information?</Label>
                    <p className='text-[#A8ADB9] text-sm'>
                      Would you like to share your fitness goals or any health conditions?
                      <span className='text-[#DC1D00]'>*</span>
                    </p>
                  </div>
                  <ToggleGroup
                    type='single'
                    value={`${watch('isPhysicalActivities')}`}
                    onValueChange={(value) =>
                      value &&
                      setValue('isPhysicalActivities', value === 'true', { shouldValidate: true })
                    }
                    className='grid grid-cols-2 gap-2 max-w-[200px]'
                  >
                    <ToggleGroupItem value='false'>No</ToggleGroupItem>
                    <ToggleGroupItem value='true'>Yes</ToggleGroupItem>
                  </ToggleGroup>
                  <CustomTextarea
                    id='physical-activites-textarea'
                    label='Describe'
                    className='bg-transparent border resize-none'
                    {...register('physicalActivities')}
                    error={errors?.physicalActivities?.message}
                  />
                  {errors?.isPhysicalActivities?.message && (
                    <p className='text-red text-xs'>
                      {errors?.isPhysicalActivities?.message === 'Required'
                        ? 'This question is required'
                        : errors?.isPhysicalActivities?.message}
                    </p>
                  )}
                </div>
              </CardWrapper>



              {/* Action Buttons */}
              <div className='flex items-center justify-between  mt-6 gap-3'>
                <Button
                  id='profile-completion-back'
                  variant='secondary'
                  size='default'
                  onClick={() => navigate(-1)}
                  type='button'
                  className='w-full'
                >
                  Back
                </Button>
                <Button
                  id='profile-completion-save'
                  variant='default'
                  size='default'
                  type='submit'
                  loading={loading}
                  className='w-full'
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>

          <ImageCropper
            setCroppedImageL={setCroppedImage}
            selectedImage={selectedImage}
            aspectRatio={1}
            open={cropperOpen}
            onClose={() => {
              setCropperOpen(false);
              if (imageRef.current) imageRef.current.value = '';
            }}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default ProfileCompletion;
